/**
 * Which unit line, if any, belongs under the booking rail's price — the
 * guide's `.bk-per`.
 *
 * Extracted from the rail because the ORDER of these rules is the whole point
 * and it regressed once already: the operator's `soldAs` suffix returned before
 * the mixed-party guard was reached, so a tour with "Per person twin share"
 * printed it over a basket holding two different rates. The rule was written
 * out in a comment directly above the code that skipped it, and nothing tested
 * it.
 *
 * The rule, mixed-party FIRST:
 *
 *  - Two or more categories selected → NO line. Two categories means two
 *    rates, and no single one is true of the party: quoting one states a price
 *    true of nobody in it, and quoting an average invents a number no one is
 *    charged. `soldAs` is not exempt — "per person, twin share" over an adult
 *    plus a child is exactly the misleading line this guards against.
 *  - Otherwise the operator's own `soldAs` suffix wins when they wrote one.
 *  - Otherwise the category's charge basis.
 *
 * Counted in CATEGORIES, never head-count: a flat-group category contributes
 * zero pax, so Adult x1 + "Cleaning fee" x1 is two categories at a head count
 * of one, and a head-count guard would sit out exactly there.
 */
export type PriceUnitLine = "none" | "sold-as" | "basis";

export function resolvePriceUnitLine({
  selectedCategoryCount,
  soldAs,
}: {
  /** How many price categories the traveller has picked a quantity for. */
  selectedCategoryCount: number;
  /** The operator's price suffix, from the builder's "Sold as" field. */
  soldAs?: string | null;
}): PriceUnitLine {
  if (selectedCategoryCount > 1) return "none";
  if (soldAs && soldAs.trim().length > 0) return "sold-as";
  return "basis";
}
